import { Button, Carousel } from 'antd';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Language } from 'types/schema';
import utilMessages from 'utils/messages';
import ProductDetailsBannerImages from '../../../containers/ProductDetailsPage/images';
import gyomuLogo from '../_icons/gyomu_super_logo.png';

const BannerContainer = styled.div`
  width: 100%;
  aspect-ratio: 423/116;
  position: relative;
  outline: none;
`;
const Banner = styled.img`
  width: 100%;
  height: 100%;
`;
const BannerText = styled.span`
  position: absolute;
  top: 16px;
  left: 20px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: #343538;
`;
const BannerGyomuSubTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const ExploreButton = styled(Button)`
  position: absolute;
  bottom: 10px;
  left: 20px;
  width: 90px;
  height: 32px;
  line-height: 32px;
  font-weight: 500 !important;
  font-size: 14px !important;
  color: #2f382c !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  border: none !important;
`;

const CarouselBannerContainer = styled(Carousel)`
  .slick-dots-bottom {
    bottom: -10px !important;
  }
  .slick-dots li {
    height: 4px !important;
    width: 24px !important;
    background: #eaebea !important;
    box-sizing: border-box !important;
    border-radius: 0px !important;
    margin-inline: 0px !important;
  }
  .slick-dots li.slick-active {
    background: #6fc373 !important;
  }

  .slick-dots li:first-child {
    border-radius: 4px 0 0 4px !important;
  }
  .slick-dots li:last-child {
    border-radius: 0 4px 4px 0 !important;
  }
  .slick-dots li::after {
    background: transparent !important;
  }
`;

const GyomuLogo = styled.img`
  width: 75px;
  height: 18px;
`;

interface ProductDetailsBannerProps {
  tags?: string[];
}

const ProductDetailsBanner = (props: ProductDetailsBannerProps) => {
  const { tags } = props;
  const lang = localStorage.getItem('lang') || Language.Vi;

  const isFarmToBusiness = tags?.some((tag) => tag?.includes('FARM'));

  if (isFarmToBusiness) {
    return (
      <BannerContainer>
        <BannerText>
          {lang === 'vi' ? (
            <>
              Xem thêm dòng sản phẩm
              <br />
              "Farm to Business"
            </>
          ) : (
            <>
              Discover our "Farm to Business"
              <br />
              product line
            </>
          )}
        </BannerText>
        <Banner
          src={ProductDetailsBannerImages.farmToBusiness1x}
          alt="Kamereo - Farm to Business Products"
          srcSet={`${ProductDetailsBannerImages.farmToBusiness1x} 1x, ${ProductDetailsBannerImages.farmToBusiness2x} 2x, ${ProductDetailsBannerImages.farmToBusiness3x} 3x`}
        />
        <Link to={lang === 'vi' ? '/tu-nong-trai-den-doanh-nghiep' : `/tu-nong-trai-den-doanh-nghiep?lang=${lang}`}>
          <ExploreButton>
            <FormattedMessage {...utilMessages.explore} />
          </ExploreButton>
        </Link>
      </BannerContainer>
    );
  }

  const isGyomuJapan = tags?.some((tag) => tag?.includes('GYOMU_SUPER'));
  if (isGyomuJapan) {
    return (
      <BannerContainer>
        <BannerText>
          {lang === 'vi' ? (
            <>
              Xem thêm dòng sản phẩm
              <br />
              <BannerGyomuSubTextContainer>
                "Gyomu Japan"
                <GyomuLogo src={gyomuLogo} alt="Gyomu Japan" loading="lazy" />
              </BannerGyomuSubTextContainer>
            </>
          ) : (
            <>
              Explore the “Gyoumu Japan"
              <br />
              <BannerGyomuSubTextContainer>
                collection
                <GyomuLogo src={gyomuLogo} alt="Gyomu Japan" loading="lazy" />
              </BannerGyomuSubTextContainer>
            </>
          )}
        </BannerText>
        <Banner
          src={ProductDetailsBannerImages.gyomuJapan1x}
          alt="Kamereo - Gyomu Japan Products"
          srcSet={`${ProductDetailsBannerImages.gyomuJapan1x} 1x, ${ProductDetailsBannerImages.gyomuJapan2x} 2x, ${ProductDetailsBannerImages.gyomuJapan3x} 3x`}
        />
        <Link
          to={lang === 'vi' ? '/nguyen-lieu-nhat-ban-gyomu-japan' : `/nguyen-lieu-nhat-ban-gyomu-japan?lang=${lang}`}
        >
          <ExploreButton>
            <FormattedMessage {...utilMessages.explore} />
          </ExploreButton>
        </Link>
      </BannerContainer>
    );
  }

  const isKamereoBrand = tags?.some((tag) => tag?.includes('KAMEREO'));
  if (isKamereoBrand) {
    return (
      <CarouselBannerContainer draggable autoplay autoplaySpeed={5000} pauseOnHover>
        <BannerContainer>
          <BannerText>
            {lang === 'vi' ? (
              <>
                Khám phá sản phẩm
                <br />
                Kamereo chất lượng
              </>
            ) : (
              <>
                Browse Kamereo's premium
                <br />
                product selection
              </>
            )}
          </BannerText>
          <Banner
            src={ProductDetailsBannerImages.kamereoLabel1x}
            alt="Kamereo - Kamereo Label"
            srcSet={`${ProductDetailsBannerImages.kamereoLabel1x} 1x, ${ProductDetailsBannerImages.kamereoLabel2x} 2x, ${ProductDetailsBannerImages.kamereoLabel3x} 3x`}
          />
          <Link to={lang === 'vi' ? '/thuong-hieu-kamereo' : `/thuong-hieu-kamereo?lang=${lang}`}>
            <ExploreButton>
              <FormattedMessage {...utilMessages.explore} />
            </ExploreButton>
          </Link>
        </BannerContainer>
        <BannerContainer>
          <BannerText>
            {lang === 'vi' ? (
              <>
                Xem các địa điểm phân phối
                <br />
                sản phẩm Kamereo
              </>
            ) : (
              <>
                Locate Kamereo's official
                <br />
                distribution point
              </>
            )}
          </BannerText>
          <Banner
            src={ProductDetailsBannerImages.kamereoDistributor1x}
            alt="Kamereo - Kamereo Distribution"
            srcSet={`${ProductDetailsBannerImages.kamereoDistributor1x} 1x, ${ProductDetailsBannerImages.kamereoDistributor2x} 2x, ${ProductDetailsBannerImages.kamereoDistributor3x} 3x`}
          />
          <Link to={lang === 'vi' ? '/he-thong-phan-phoi' : `/he-thong-phan-phoi?lang=${lang}`}>
            <ExploreButton>
              <FormattedMessage {...utilMessages.viewDetail} />
            </ExploreButton>
          </Link>
        </BannerContainer>
      </CarouselBannerContainer>
    );
  }
  return null;
};
export default memo(ProductDetailsBanner);
